import React, { useState, useEffect } from "react";
import "./projectShowcase.css";
import ShowcaseCard, { mediaUrl } from "./ShowcaseCard";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";

const API = "https://hr-backend-72v1.onrender.com";
const isImage = (m) => (m.mimeType || "").startsWith("image/");

const emptyForm = {
  title: "",
  clientName: "",
  description: "",
  startDate: "",
  endDate: "",
  services: [],
  teamMembers: [],
  media: [],
  mediaLinks: [],
};

// Manager-only view: create / edit / delete the manager's OWN showcase entries,
// plus a list of those entries. Lives inside the Employee container, gated by
// `manager === "yes"`. Reuses the Drive upload flow from the Documents feature.
const ProjectShowcaseManage = ({ user }) => {
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [tagInput, setTagInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [teamOptions, setTeamOptions] = useState([]); // employees under this manager

  const fetchEntries = async () => {
    try {
      const res = await fetch(
        `${API}/project-showcase?email=${encodeURIComponent(user.email)}`
      );
      const data = await res.json();
      // The backend scopes this to the caller's team (own + teammates + team lead),
      // or everything for view-all roles. Your own entries are editable below;
      // teammates'/lead's entries render read-only.
      setEntries(Array.isArray(data) ? data : []);
    } catch (e) {
      setEntries([]);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [user.email]);

  // Auto-load the caller's team (teammates + lead) so members can be picked
  // instead of typed. Works for every employee, not just managers.
  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const res = await fetch(
          `${API}/project-showcase/team?email=${encodeURIComponent(user.email)}`
        );
        const data = await res.json();
        setTeamOptions(Array.isArray(data) ? data : []);
      } catch (e) {
        setTeamOptions([]);
      }
    };
    fetchTeam();
  }, [user.email]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const resetForm = () => {
    setForm(emptyForm);
    setTagInput("");
    setEditingId(null);
    setShowForm(false);
  };

  const openCreate = () => {
    setForm(emptyForm);
    setTagInput("");
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (entry) => {
    setForm({
      title: entry.title || "",
      clientName: entry.clientName || "",
      description: entry.description || "",
      startDate: entry.startDate ? entry.startDate.substring(0, 10) : "",
      endDate: entry.endDate ? entry.endDate.substring(0, 10) : "",
      services: entry.services || [],
      teamMembers: entry.teamMembers || [],
      media: entry.media || [],
      mediaLinks: entry.mediaLinks || [],
    });
    setEditingId(entry._id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // --- tags ---
  const addTag = () => {
    const t = tagInput.trim();
    if (t && !form.services.includes(t)) {
      setField("services", [...form.services, t]);
    }
    setTagInput("");
  };
  const removeTag = (t) =>
    setField("services", form.services.filter((s) => s !== t));

  // --- team members (picked from the manager's team, not typed) ---
  const toggleMember = (m) => {
    setForm((f) => {
      const exists = f.teamMembers.some((t) => t.email === m.email);
      return {
        ...f,
        teamMembers: exists
          ? f.teamMembers.filter((t) => t.email !== m.email)
          : [...f.teamMembers, { name: m.name, email: m.email }],
      };
    });
  };

  // --- links ---
  const addLink = () =>
    setField("mediaLinks", [...form.mediaLinks, { label: "", url: "" }]);
  const updateLink = (i, k, v) => {
    const next = form.mediaLinks.map((l, idx) =>
      idx === i ? { ...l, [k]: v } : l
    );
    setField("mediaLinks", next);
  };
  const removeLink = (i) =>
    setField("mediaLinks", form.mediaLinks.filter((_, idx) => idx !== i));

  // --- media (Drive upload) ---
  const onFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("email", user.email);
      try {
        const res = await fetch(`${API}/project-showcase/upload`, {
          method: "POST",
          body: fd,
        });
        if (res.ok) {
          const ref = await res.json();
          setForm((f) => ({ ...f, media: [...f.media, ref] }));
        }
      } catch (_) {
        /* ignore individual failures */
      }
    }
    setUploading(false);
    e.target.value = "";
  };
  const removeMedia = (i) =>
    setField("media", form.media.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      alert("Please enter a project title.");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      email: user.email, // used for auth on update
      ownerEmail: user.email,
      teamMembers: form.teamMembers.filter((m) => m.name || m.email),
      mediaLinks: form.mediaLinks.filter((l) => l.url),
      startDate: form.startDate || undefined,
      endDate: form.endDate || undefined,
    };
    try {
      const url = editingId
        ? `${API}/project-showcase/${editingId}`
        : `${API}/project-showcase`;
      const res = await fetch(url, {
        method: editingId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        resetForm();
        fetchEntries();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.message || "Failed to save project.");
      }
    } catch (e) {
      alert("Failed to save project.");
    }
    setSaving(false);
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete "${entry.title}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/project-showcase/${entry._id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: user.email }),
      });
      if (res.ok) fetchEntries();
      else alert("Failed to delete project.");
    } catch (e) {
      alert("Failed to delete project.");
    }
  };

  // Team members to display in the picker: the manager's current team, plus any
  // already-selected members who may no longer be on the team (so they stay
  // visible/removable when editing an older entry).
  const displayMembers = [
    ...teamOptions,
    ...form.teamMembers.filter(
      (t) => !teamOptions.some((o) => o.email === t.email)
    ),
  ];

  return (
    <div className="psWrap">
      <header className="psHeader">
        <div>
          <h1 className="psTitle">Project Showcase</h1>
          <p className="psSubtitle">
            Add projects you've delivered — everyone on your team can see each
            other's work
          </p>
        </div>
        {!showForm && (
          <button className="psPrimaryBtn" onClick={openCreate}>
            <AddOutlinedIcon fontSize="small" />
            New Project
          </button>
        )}
      </header>

      {showForm && (
        <div className="psForm">
          <h2 className="psFormTitle">
            {editingId ? "Edit Project" : "Add a Project"}
          </h2>

          <div className="psFormGrid">
            <div className="psField">
              <span className="psLabel">Project Title *</span>
              <input
                className="psInput"
                value={form.title}
                onChange={(e) => setField("title", e.target.value)}
                placeholder="e.g. E-commerce site for Acme Co."
              />
            </div>
            <div className="psField">
              <span className="psLabel">Client Name</span>
              <input
                className="psInput"
                value={form.clientName}
                onChange={(e) => setField("clientName", e.target.value)}
                placeholder="e.g. Acme Co."
              />
            </div>
            <div className="psField">
              <span className="psLabel">Project Start Date</span>
              <input
                type="date"
                className="psInput"
                value={form.startDate}
                onChange={(e) => setField("startDate", e.target.value)}
              />
            </div>
            <div className="psField">
              <span className="psLabel">Project End Date</span>
              <input
                type="date"
                className="psInput"
                value={form.endDate}
                onChange={(e) => setField("endDate", e.target.value)}
              />
            </div>
            <div className="psField">
              <span className="psLabel">Add Service / Tech Tag</span>
              <div className="psRow">
                <input
                  className="psInput"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  placeholder="e.g. SEO - press Enter"
                />
                <button className="psAddBtn" onClick={addTag} type="button">
                  <AddOutlinedIcon fontSize="small" />
                  Add
                </button>
              </div>
            </div>
            <div className="psField psField--full">
              <span className="psLabel">Description</span>
              <textarea
                className="psTextarea"
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                placeholder="What did your team deliver? Results, scope, highlights…"
              />
            </div>
          </div>

          {form.services.length > 0 && (
            <div className="psTags">
              {form.services.map((s) => (
                <span className="psTag" key={s}>
                  {s}
                  <button type="button" onClick={() => removeTag(s)}>
                    <CloseOutlinedIcon />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Team members - picked from the manager's own team */}
          <div>
            <div className="psSectionLabel">
              Team Members
              {form.teamMembers.length > 0 && (
                <span className="psSectionCount"> · {form.teamMembers.length} selected</span>
              )}
            </div>
            {displayMembers.length === 0 ? (
              <div className="psUploadHint">
                No teammates to tag yet — you can still publish the project on its
                own.
              </div>
            ) : (
              <div className="psMemberPick">
                {displayMembers.map((m) => {
                  const sel = form.teamMembers.some((t) => t.email === m.email);
                  return (
                    <button
                      type="button"
                      key={m.email || m.name}
                      className={`psMemberOpt ${sel ? "psMemberOpt--on" : ""}`}
                      onClick={() => toggleMember(m)}
                    >
                      <span className="psMemberOptCheck">
                        {sel && <CheckOutlinedIcon sx={{ fontSize: 13 }} />}
                      </span>
                      <span className="psMemberOptText">
                        <span className="psMemberOptName">{m.name}</span>
                        <span className="psMemberOptEmail">{m.email}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* External media links */}
          <div>
            <div className="psSectionLabel">Video / Loom Links</div>
            {form.mediaLinks.map((l, i) => (
              <div className="psRow" key={i} style={{ marginBottom: 10 }}>
                <input
                  className="psInput"
                  value={l.label}
                  onChange={(e) => updateLink(i, "label", e.target.value)}
                  placeholder="Label (e.g. Loom walkthrough)"
                />
                <input
                  className="psInput"
                  value={l.url}
                  onChange={(e) => updateLink(i, "url", e.target.value)}
                  placeholder="https://…"
                />
                <button
                  className="psIconBtn"
                  type="button"
                  onClick={() => removeLink(i)}
                >
                  <DeleteOutlineOutlinedIcon fontSize="small" />
                </button>
              </div>
            ))}
            <button className="psAddBtn" type="button" onClick={addLink}>
              <AddOutlinedIcon fontSize="small" />
              Add Link
            </button>
          </div>

          {/* File uploads */}
          <div>
            <div className="psSectionLabel">
              Screenshots / Recordings / Documents
            </div>
            <div className="psUpload">
              <label className="psChooseBtn">
                <FileUploadOutlinedIcon sx={{ fontSize: 16 }} />
                Choose Files
                <input
                  type="file"
                  multiple
                  hidden
                  onChange={onFilesSelected}
                  disabled={uploading}
                />
              </label>
              <span className="psUploadHint">
                {uploading
                  ? "Uploading…"
                  : "Images, PDFs, recordings - uploaded to Google Drive"}
              </span>
            </div>
            {form.media.length > 0 && (
              <div className="psMedia" style={{ marginTop: 12 }}>
                {form.media.map((m, i) => (
                  <div
                    className={isImage(m) ? "psThumb" : "psThumbFile"}
                    key={i}
                    title={m.fileName}
                  >
                    {isImage(m) ? (
                      <img src={mediaUrl(m.fileId)} alt={m.fileName} />
                    ) : (
                      <>
                        <InsertDriveFileOutlinedIcon />
                        <span className="psThumbFileName">{m.fileName}</span>
                      </>
                    )}
                    <button
                      className="psThumbRemove"
                      type="button"
                      onClick={() => removeMedia(i)}
                    >
                      <CloseOutlinedIcon />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="psFormActions">
            <button className="psGhostBtn" onClick={resetForm} type="button">
              Cancel
            </button>
            <button
              className="psPrimaryBtn"
              onClick={handleSubmit}
              disabled={saving || uploading}
            >
              {saving
                ? "Saving…"
                : editingId
                ? "Save Changes"
                : "Publish Project"}
            </button>
          </div>
        </div>
      )}

      {entries.length > 0 ? (
        <div className="psGrid">
          {entries.map((e) => {
            const owned = e.ownerEmail === user.email;
            return (
              <ShowcaseCard
                key={e._id}
                entry={e}
                onEdit={owned ? openEdit : undefined}
                onDelete={owned ? handleDelete : undefined}
              />
            );
          })}
        </div>
      ) : (
        !showForm && (
          <div className="psEmpty">
            <span className="psEmptyIcon">
              <CollectionsBookmarkOutlinedIcon />
            </span>
            <div>
              <div className="psEmptyTitle">No projects added yet</div>
              <div className="psEmptySub">
                Click “New Project” to add your team’s first delivered project.
              </div>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default ProjectShowcaseManage;
